import {
  IconBell,
  IconCalendar,
  IconMessage,
  IconUsers,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { NotificationOutput } from "@/api-types";
import { createPath, UniversalRoutes } from "@/routers/routes";

export const renderNotificationLink = ({
  notification,
}: {
  notification: NotificationOutput;
}): string => {
  const engagementId = notification.engagementId;
  const objectId = notification.objectId;
  const routes = UniversalRoutes;

  switch (notification.type) {
    case "CHAT_MESSAGE":
      if (engagementId) {
        return createPath({
          path: routes.CHAT.DETAIL,
          params: { engagementId },
        });
      }
      break;
    case "GROUP_CHAT_MESSAGE":
      if (engagementId) {
        return createPath({
          path: routes.GROUP_CHAT.DETAIL,
          params: { groupId: engagementId },
        });
      }
      break;
    case "SESSION_REQUESTED":
    case "SESSION_CONFIRMED":
    case "SESSION_COMPLETED":
    case "SESSION_CANCELLED":
    case "SESSION_UPDATE":
      if (objectId) {
        return createPath({
          path: routes.SESSIONS.DETAIL,
          params: { sessionId: objectId },
        });
      }
      break;
    case "GROUP_JOIN_REQUESTED":
    case "GROUP_JOIN_APPROVED":
    case "GROUP_JOIN_REJECTED":
      if (objectId) {
        return createPath({
          path: routes.GROUPS.DETAIL,
          params: { groupId: objectId },
        });
      }
      break;
  }
  return "/";
};

export const getNotificationIcon = (type: string) => {
  switch (type) {
    case "CHAT_MESSAGE":
      return <IconMessage size={16} />;
    case "GROUP_CHAT_MESSAGE":
      return <IconUsers size={16} />;
    case "SESSION_REQUESTED":
    case "SESSION_CONFIRMED":
    case "SESSION_COMPLETED":
    case "SESSION_CANCELLED":
    case "SESSION_UPDATE":
      return <IconCalendar size={16} />;
    case "GROUP_JOIN_REQUESTED":
    case "GROUP_JOIN_APPROVED":
    case "GROUP_JOIN_REJECTED":
      return <IconUsers size={16} />;
    default:
      return <IconBell size={16} />;
  }
};

export const renderNotificationTitle = (
  notification: NotificationOutput,
): string => {
  switch (notification.type) {
    case "CHAT_MESSAGE":
      return "New chat message";
    case "GROUP_CHAT_MESSAGE":
      return "New group message";
    case "SESSION_REQUESTED":
      return "Session Requested";
    case "SESSION_CONFIRMED":
      return "Session Confirmed";
    case "SESSION_COMPLETED":
      return "Session Completed";
    case "SESSION_CANCELLED":
      return "Session Cancelled";
    case "SESSION_UPDATE":
      return "Session Updated";
    case "GROUP_JOIN_REQUESTED":
      return "Group Join Request";
    case "GROUP_JOIN_APPROVED":
      return "Group Join Approved";
    case "GROUP_JOIN_REJECTED":
      return "Group Join Rejected";
    default:
      return "Update from Woofapy";
  }
};

export const renderNotificationMessage = (
  notification: NotificationOutput,
): ReactNode => {
  if (notification.message) {
    return `${notification.senderDisplayName}: ${notification.message}`;
  }

  switch (notification.type) {
    case "CHAT_MESSAGE":
      return `${notification.senderDisplayName} sent you a message.`;
    case "GROUP_CHAT_MESSAGE":
      return `${notification.senderDisplayName} sent a group message.`;
    case "SESSION_REQUESTED":
      return `${notification.senderDisplayName} sent you a session request.`;
    case "SESSION_CONFIRMED":
      return `${notification.senderDisplayName} confirmed a session.`;
    case "SESSION_COMPLETED":
      return `You completed a session with ${notification.senderDisplayName}.`;
    case "SESSION_CANCELLED":
      return `${notification.senderDisplayName} cancelled a session.`;
    case "SESSION_UPDATE":
      return `${notification.senderDisplayName} updated a session.`;
    case "GROUP_JOIN_REQUESTED":
      return `${notification.senderDisplayName} requested to join a group.`;
    case "GROUP_JOIN_APPROVED":
      return `${notification.senderDisplayName} approved your group join request.`;
    case "GROUP_JOIN_REJECTED":
      return `${notification.senderDisplayName} rejected your group join request.`;
    default:
      return "New notification";
  }
};
